import { useEffect, useMemo, useState } from 'react'
import {
  Modal, Stepper, Stack, Group, Button, Text, Alert, Select, TextInput,
  NumberInput, MultiSelect, TagsInput, Textarea, Code, ScrollArea, Loader,
  SegmentedControl, Autocomplete,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconArrowRight, IconCheck, IconAlertTriangle } from '@tabler/icons-react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { formatApiError } from '@/api/errors'
import { createWafRule, listManagedRuleGroups, type WafRuleKind } from '@/api/waf'
import type { WafContext } from './wafContext'
import { PROXIMO_PASSO_GMUD } from '@/components/ZtnaAdmin/gmudNotice'

// ─── Tipos ───────────────────────────────────────────────────────────────────

// `kind` curado que o wizard sabe montar (subconjunto de WafRuleKind sem 'other').
type WizardKind = Exclude<WafRuleKind, 'other'>

interface CreateWafRuleResponse {
  status: 'applied' | 'pending_gmud'
  gmud_id?: number
  name?: string
}

interface WafRuleWizardIpSet {
  ipset_id: string
  ipset_arn?: string
  name?: string
  kind?: string
}

interface WafRuleWizardProps {
  wafId: number
  opened: boolean
  onClose: () => void
  ipsets?: WafRuleWizardIpSet[]
  /** Domínios protegidos por este WAF (sugestões do escopo por domínio). */
  domains?: string[]
  /** Contexto do workspace por domínio ativo (pré-preenche o escopo da regra). */
  context?: WafContext
  /** Lista de domínios do recurso pode estar incompleta (apenas no contexto de recurso). */
  domainsPartial?: boolean
  onCreated?: () => void
}

// ─── Constantes ──────────────────────────────────────────────────────────────

const KIND_OPTIONS: { value: WizardKind; label: string; help: string }[] = [
  { value: 'managed', label: 'Managed group',    help: 'Grupo de regras gerenciado pela AWS/fornecedor.' },
  { value: 'rate',    label: 'Rate-based',       help: 'Limita requisições por IP num período de 5 minutos.' },
  { value: 'ip',      label: 'IP (block)',       help: 'Casa um IPSet existente deste WAF.' },
  { value: 'geo',     label: 'Geo (block)',      help: 'Casa por país de origem (ISO-2).' },
  { value: 'byte',    label: 'String',           help: 'Casamento de string em um campo da requisição.' },
  { value: 'regex',   label: 'Regex',            help: 'Casamento por expressão regular em um campo.' },
  { value: 'json',    label: 'JSON bruto',       help: 'Statement wafv2 cru (avançado).' },
  { value: 'allow',   label: 'Allow (exceção)',  help: 'Permite (bypassa o WAF) requisições cujo URI casa exatamente. Abre um buraco — use só para probes/monitores conhecidos.' },
]

const FIELD_OPTIONS = [
  { value: 'UriPath', label: 'URI Path' },
  { value: 'QueryString', label: 'Query String' },
  { value: 'SingleHeader', label: 'Header (único)' },
  { value: 'Method', label: 'Método' },
  { value: 'Body', label: 'Corpo' },
]

const BYTE_OP_OPTIONS = [
  { value: 'CONTAINS', label: 'Contém' },
  { value: 'STARTS_WITH', label: 'Começa com' },
  { value: 'ENDS_WITH', label: 'Termina com' },
  { value: 'EXACTLY', label: 'Exatamente' },
]

const TRANSFORM_OPTIONS = [
  { value: 'NONE', label: 'Nenhuma' },
  { value: 'LOWERCASE', label: 'Minúsculas' },
  { value: 'URL_DECODE', label: 'URL decode' },
]

const AGGREGATE_OPTIONS = [
  { value: 'IP', label: 'IP do cliente' },
  { value: 'FORWARDED_IP', label: 'IP encaminhado (X-Forwarded-For)' },
]

const NAME_RE = /^[A-Za-z0-9_-]{1,128}$/

// ─── Componente ──────────────────────────────────────────────────────────────

export function WafRuleWizard({ wafId, opened, onClose, ipsets, domains, context, domainsPartial, onCreated }: WafRuleWizardProps) {
  const ctx: WafContext = context ?? { kind: 'none' }
  const [active, setActive] = useState(0)
  const [kind, setKind] = useState<WizardKind>('managed')

  // managed
  const [managedSel, setManagedSel] = useState<string | null>(null)
  const [managedVersion, setManagedVersion] = useState('')

  // rate
  const [rateLimit, setRateLimit] = useState<number | string>(2000)
  const [rateAggregate, setRateAggregate] = useState<string>('IP')

  // ip
  const [ipArn, setIpArn] = useState<string | null>(null)

  // geo
  const [countries, setCountries] = useState<string[]>([])

  // byte / regex
  const [field, setField] = useState<string>('UriPath')
  const [header, setHeader] = useState('')
  const [byteOp, setByteOp] = useState<string>('CONTAINS')
  const [matchValue, setMatchValue] = useState('')
  const [transforms, setTransforms] = useState<string[]>(['NONE'])

  // json
  const [jsonText, setJsonText] = useState('')

  // allow
  const [allowUris, setAllowUris] = useState<string[]>([])

  // ação + prioridade
  const [name, setName] = useState('')
  const [priority, setPriority] = useState<number | string>('')
  const [reason, setReason] = useState('')

  // escopo (host-scoping):
  //  - sem contexto:    'all' = todos os domínios do WAF | 'specific' = um domínio
  //  - contexto recurso:'all' = todos os hosts do recurso | 'specific' = um host
  //  - contexto host:   sempre 'specific', host pré-travado (mas editável)
  const [applyTo, setApplyTo] = useState<'all' | 'specific'>('all')
  // Modelo multi-host canônico. No modo sem-contexto carrega no máximo 1 host.
  const [scopeHosts, setScopeHosts] = useState<string[]>([])

  // Pré-preenche o escopo a partir do contexto quando o modal abre.
  useEffect(() => {
    if (!opened) return
    if (ctx.kind === 'host') {
      setApplyTo('specific')
      setScopeHosts([ctx.host])
    } else if (ctx.kind === 'resource') {
      // 'all' = todos os hosts deste recurso (submete a lista inteira).
      // Se o recurso não tem hosts conhecidos, força entrada manual ('specific').
      setApplyTo(ctx.hosts.length ? 'all' : 'specific')
      setScopeHosts([])
    } else {
      setApplyTo('all')
      setScopeHosts([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, context])

  const [submitError, setSubmitError] = useState<string | null>(null)

  // Managed rule groups — só busca quando o tipo managed está selecionado e o modal aberto.
  const mrgQuery = useQuery({
    queryKey: ['waf-mrg', wafId],
    queryFn: () => listManagedRuleGroups(wafId),
    enabled: opened && kind === 'managed',
    staleTime: 5 * 60_000,
  })

  const managedOptions = useMemo(() => {
    const groups = mrgQuery.data?.groups ?? []
    return groups
      .filter(g => g.VendorName && g.Name)
      .map(g => ({
        value: `${g.VendorName}|${g.Name}`,
        label: `${g.VendorName} · ${g.Name}`,
      }))
  }, [mrgQuery.data])

  const ipOptions = useMemo(
    () =>
      (ipsets ?? [])
        .filter(s => s.ipset_arn)
        .map(s => ({ value: s.ipset_arn as string, label: s.name || (s.ipset_arn as string) })),
    [ipsets],
  )

  // Domínios deste WAF (dedup + sort) para sugestão no escopo por domínio.
  const domainOptions = useMemo(
    () => Array.from(new Set((domains ?? []).map(d => d.trim()).filter(Boolean))).sort(),
    [domains],
  )

  // Validação client-side do JSON bruto.
  const jsonParse = useMemo<{ ok: boolean; value?: unknown; error?: string }>(() => {
    const raw = jsonText.trim()
    if (!raw) return { ok: false, error: 'Informe o statement JSON.' }
    try {
      return { ok: true, value: JSON.parse(raw) }
    } catch (e) {
      return { ok: false, error: `JSON inválido: ${(e as Error).message}` }
    }
  }, [jsonText])

  // Monta os params na forma exata esperada pelo backend `build_statement`.
  function buildParams(): Record<string, unknown> | null {
    switch (kind) {
      case 'managed': {
        if (!managedSel) return null
        const [vendor, gname] = managedSel.split('|')
        const p: Record<string, unknown> = { vendor, name: gname }
        if (managedVersion.trim()) p.version = managedVersion.trim()
        return p
      }
      case 'rate':
        return { limit: typeof rateLimit === 'number' ? rateLimit : Number(rateLimit), aggregate: rateAggregate }
      case 'ip':
        if (!ipArn) return null
        return { ipset_arn: ipArn }
      case 'geo':
        if (countries.length === 0) return null
        return { countries }
      case 'byte':
        if (!matchValue.trim()) return null
        return {
          field,
          ...(field === 'SingleHeader' ? { header: header.trim() } : {}),
          op: byteOp,
          value: matchValue,
          transforms: transforms.length ? transforms : ['NONE'],
        }
      case 'regex':
        if (!matchValue.trim()) return null
        return {
          field,
          ...(field === 'SingleHeader' ? { header: header.trim() } : {}),
          value: matchValue,
          transforms: transforms.length ? transforms : ['NONE'],
        }
      case 'json':
        if (!jsonParse.ok) return null
        return { statement: jsonParse.value }
      case 'allow': {
        const uris = allowUris.map((u) => u.trim()).filter(Boolean)
        if (uris.length === 0) return null
        return { uris }
      }
      default:
        return null
    }
  }

  const params = buildParams()

  // Validações por passo (habilita o botão "Próximo"/"Criar").
  const configValid = (() => {
    switch (kind) {
      case 'managed':
        return !!managedSel
      case 'rate':
        return (typeof rateLimit === 'number' ? rateLimit : Number(rateLimit)) >= 100
      case 'ip':
        return !!ipArn
      case 'geo':
        return countries.length > 0
      case 'byte':
      case 'regex':
        return !!matchValue.trim() && (field !== 'SingleHeader' || !!header.trim())
      case 'json':
        return jsonParse.ok
      case 'allow':
        return allowUris.map((u) => u.trim()).filter(Boolean).length > 0
      default:
        return false
    }
  })()

  const nameValid = NAME_RE.test(name)

  // Hosts efetivos a aplicar (lista deduplicada, sem vazios).
  const effectiveScopeHosts = useMemo<string[]>(() => {
    let hosts: string[]
    if (ctx.kind === 'resource' && applyTo === 'all') {
      // "Todos os hosts deste recurso" → submete a lista inteira do recurso.
      hosts = ctx.hosts
    } else if (applyTo === 'specific') {
      hosts = scopeHosts
    } else {
      // 'all' sem contexto / contexto host genérico → sem escopo (todos do WAF).
      hosts = []
    }
    return Array.from(new Set(hosts.map(h => h.trim()).filter(Boolean)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, applyTo, scopeHosts])

  // No modo 'specific' (ou contexto host) a regra precisa de ≥1 host.
  const scopeValid = applyTo === 'all' ? true : effectiveScopeHosts.length > 0
  const actionValid = nameValid && scopeValid

  const priorityNumber =
    priority === '' || priority === null ? undefined : Number(priority)

  const previewObject = {
    kind,
    params: params ?? {},
    name,
    ...(priorityNumber !== undefined ? { priority: priorityNumber } : {}),
    ...(effectiveScopeHosts.length ? { scope_hosts: effectiveScopeHosts } : {}),
  }

  const createMutation = useMutation({
    mutationFn: () =>
      createWafRule(wafId, {
        kind,
        params: params ?? {},
        name,
        // Regra Allow: backend fixa prioridade 0 — não enviar priority.
        ...(kind !== 'allow' ? { priority: priorityNumber } : {}),
        reason: reason.trim() || undefined,
        // scope_hosts tem precedência; só enviamos quando há ≥1 host.
        ...(effectiveScopeHosts.length ? { scope_hosts: effectiveScopeHosts } : {}),
      }),
    onSuccess: (data) => {
      setSubmitError(null)
      const resp = data as CreateWafRuleResponse | undefined
      if (resp?.status === 'pending_gmud') {
        notifications.show({
          color: 'blue',
          title: 'Regra Allow registrada',
          message: `"${name}" aguarda aprovação via GMUD (#${resp.gmud_id ?? '—'}). ${PROXIMO_PASSO_GMUD}`,
          icon: <IconCheck size={16} />,
        })
      } else {
        notifications.show({
          color: 'green',
          title: 'Regra criada',
          message: `"${name}" foi criada em modo Count. Promova para Block quando validada.`,
          icon: <IconCheck size={16} />,
        })
      }
      handleClose()
      onCreated?.()
    },
    onError: (err: unknown) => {
      setSubmitError(formatApiError(err, 'Erro ao criar regra.'))
    },
  })

  function resetState() {
    setActive(0)
    setKind('managed')
    setManagedSel(null)
    setManagedVersion('')
    setRateLimit(2000)
    setRateAggregate('IP')
    setIpArn(null)
    setCountries([])
    setField('UriPath')
    setHeader('')
    setByteOp('CONTAINS')
    setMatchValue('')
    setTransforms(['NONE'])
    setJsonText('')
    setAllowUris([])
    setName('')
    setPriority('')
    setReason('')
    setApplyTo('all')
    setScopeHosts([])
    setSubmitError(null)
  }

  function handleClose() {
    resetState()
    onClose()
  }

  function next() {
    setActive(a => Math.min(a + 1, 3))
  }
  function back() {
    setActive(a => Math.max(a - 1, 0))
  }

  const canAdvance =
    active === 0 ? true : active === 1 ? configValid : active === 2 ? actionValid : true

  return (
    <Modal opened={opened} onClose={handleClose} title="Criar regra" size="xl" closeOnClickOutside={false}>
      <Stepper active={active} onStepClick={setActive} size="sm" allowNextStepsSelect={false}>
        {/* ─── Passo 1: Tipo ─────────────────────────────────────────────── */}
        <Stepper.Step label="Tipo" description="O que a regra casa">
          <Stack gap="sm" mt="md">
            <Text size="sm" c="dimmed">Selecione o tipo de regra.</Text>
            <SegmentedControl
              fullWidth
              orientation="vertical"
              value={kind}
              onChange={v => setKind(v as WizardKind)}
              data={KIND_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
            />
            <Alert color="gray" variant="light">
              {KIND_OPTIONS.find(o => o.value === kind)?.help}
            </Alert>
          </Stack>
        </Stepper.Step>

        {/* ─── Passo 2: Config ───────────────────────────────────────────── */}
        <Stepper.Step label="Configuração" description="Parâmetros do tipo">
          <Stack gap="md" mt="md">
            {kind === 'managed' && (
              <>
                {mrgQuery.isLoading ? (
                  <Group gap="xs"><Loader size="sm" /><Text size="sm" c="dimmed">Carregando grupos gerenciados…</Text></Group>
                ) : mrgQuery.isError ? (
                  <Alert color="red">{formatApiError(mrgQuery.error, 'Erro ao carregar grupos gerenciados.')}</Alert>
                ) : (
                  <Select
                    label="Grupo gerenciado"
                    placeholder="Selecione (fornecedor · nome)"
                    data={managedOptions}
                    value={managedSel}
                    onChange={setManagedSel}
                    searchable
                    nothingFoundMessage="Nenhum grupo disponível"
                  />
                )}
                <TextInput
                  label="Versão (opcional)"
                  placeholder="Ex.: Version_1.0 — vazio usa a versão padrão"
                  value={managedVersion}
                  onChange={e => setManagedVersion(e.currentTarget.value)}
                />
              </>
            )}

            {kind === 'rate' && (
              <>
                <NumberInput
                  label="Limite (req. por 5 min, por chave)"
                  value={rateLimit}
                  onChange={setRateLimit}
                  min={100}
                  step={100}
                />
                <Select
                  label="Chave de agregação"
                  data={AGGREGATE_OPTIONS}
                  value={rateAggregate}
                  onChange={v => setRateAggregate(v ?? 'IP')}
                />
              </>
            )}

            {kind === 'ip' && (
              ipOptions.length === 0 ? (
                <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                  Nenhum IPSet com ARN disponível neste WAF.
                </Alert>
              ) : (
                <Select
                  label="IPSet"
                  placeholder="Selecione o IPSet"
                  data={ipOptions}
                  value={ipArn}
                  onChange={setIpArn}
                  searchable
                />
              )
            )}

            {kind === 'geo' && (
              <TagsInput
                label="Países (ISO-2)"
                description="Digite o código de 2 letras e pressione Enter (ex.: BR, US, RU)"
                placeholder="BR"
                value={countries}
                onChange={vals => setCountries(vals.map(v => v.trim().toUpperCase()).filter(Boolean))}
              />
            )}

            {(kind === 'byte' || kind === 'regex') && (
              <>
                <Select
                  label="Campo"
                  data={FIELD_OPTIONS}
                  value={field}
                  onChange={v => setField(v ?? 'UriPath')}
                />
                {field === 'SingleHeader' && (
                  <TextInput
                    label="Nome do header"
                    placeholder="Ex.: User-Agent"
                    value={header}
                    onChange={e => setHeader(e.currentTarget.value)}
                  />
                )}
                {kind === 'byte' && (
                  <Select
                    label="Operador"
                    data={BYTE_OP_OPTIONS}
                    value={byteOp}
                    onChange={v => setByteOp(v ?? 'CONTAINS')}
                  />
                )}
                <TextInput
                  label={kind === 'byte' ? 'Valor (string)' : 'Expressão regular'}
                  placeholder={kind === 'byte' ? 'Ex.: /admin' : 'Ex.: (?i)union\\s+select'}
                  value={matchValue}
                  onChange={e => setMatchValue(e.currentTarget.value)}
                />
                <MultiSelect
                  label="Transformações"
                  data={TRANSFORM_OPTIONS}
                  value={transforms}
                  onChange={setTransforms}
                />
              </>
            )}

            {kind === 'json' && (
              <>
                <Textarea
                  label="Statement (JSON wafv2)"
                  placeholder='{"ByteMatchStatement": { ... }}'
                  value={jsonText}
                  onChange={e => setJsonText(e.currentTarget.value)}
                  autosize
                  minRows={6}
                  maxRows={16}
                  styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                {!jsonParse.ok && jsonText.trim() && (
                  <Alert color="red" icon={<IconAlertTriangle size={16} />}>{jsonParse.error}</Alert>
                )}
              </>
            )}

            {kind === 'allow' && (
              <>
                <TagsInput
                  label="URIs (match exato, ex.: /basic_status)"
                  description="Digite o URI e pressione Enter para adicionar (ex.: /basic_status, /health)"
                  placeholder="/basic_status"
                  value={allowUris}
                  onChange={setAllowUris}
                />
                <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
                  Atenção: uma regra Allow contorna TODO o WAF para os URIs listados. Em WAF sensível, a criação gera um GMUD para aprovação.
                </Alert>
              </>
            )}
          </Stack>
        </Stepper.Step>

        {/* ─── Passo 3: Ação + prioridade ────────────────────────────────── */}
        <Stepper.Step label="Ação" description="Nome e prioridade">
          <Stack gap="md" mt="md">
            {kind === 'allow' ? (
              <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
                Ação: <b>Allow</b> — a regra bypassa o WAF inteiramente para os URIs informados. Prioridade fixada em 0 pelo backend.
              </Alert>
            ) : (
              <Alert color="blue">
                Ação: <b>Count</b> (promova p/ Block depois). A regra é criada em modo observação.
              </Alert>
            )}
            <TextInput
              label="Nome"
              placeholder="Ex.: bloqueio-uri-admin"
              value={name}
              onChange={e => setName(e.currentTarget.value)}
              error={name && !nameValid ? 'Use apenas [A-Za-z0-9_-], 1 a 128 caracteres.' : undefined}
              withAsterisk
            />
            {kind !== 'allow' && (
              <NumberInput
                label="Prioridade (opcional)"
                description="Vazio = o backend sugere a próxima prioridade disponível."
                value={priority}
                onChange={setPriority}
                min={0}
              />
            )}
            <TextInput
              label="Motivo (opcional)"
              placeholder="Registro de auditoria"
              value={reason}
              onChange={e => setReason(e.currentTarget.value)}
            />

            {/* Escopo: o rótulo de "all" depende do contexto do workspace. */}
            <Stack gap="xs">
              <Text size="sm" fw={500}>Aplicar a:</Text>

              {ctx.kind === 'host' ? (
                // ── Contexto host: host pré-travado, mas editável ──────────────
                <>
                  <TagsInput
                    label="Host"
                    description="Pré-selecionado pelo domínio aberto. Você pode editar ou adicionar hosts (ex.: *.tectoy.com.br)."
                    placeholder="Adicionar host"
                    value={scopeHosts}
                    onChange={vals => setScopeHosts(vals.map(v => v.trim()).filter(Boolean))}
                    error={effectiveScopeHosts.length === 0 ? 'Informe ao menos um host.' : undefined}
                    withAsterisk
                  />
                </>
              ) : ctx.kind === 'resource' ? (
                // ── Contexto recurso: "todos os hosts do recurso" vs um host ───
                <>
                  <SegmentedControl
                    fullWidth
                    value={applyTo}
                    onChange={v => setApplyTo(v as 'all' | 'specific')}
                    data={[
                      {
                        value: 'all',
                        label: 'Todos os hosts deste recurso',
                        disabled: ctx.hosts.length === 0,
                      },
                      { value: 'specific', label: 'Escolher um host' },
                    ]}
                  />
                  {ctx.hosts.length === 0 && (
                    <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                      Sem host, a regra valeria para TODOS os domínios deste WAF.
                    </Alert>
                  )}
                  {ctx.hosts.length > 0 && domainsPartial && (
                    <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                      Lista de domínios incompleta — "todos os hosts" pode não cobrir domínios não listados.
                    </Alert>
                  )}
                  {applyTo === 'all' && ctx.hosts.length > 0 && (
                    <Alert color="grape" variant="light">
                      Aplica aos {ctx.hosts.length} host(s) deste recurso: <b>{ctx.hosts.join(', ')}</b>
                    </Alert>
                  )}
                  {applyTo === 'specific' && (
                    ctx.hosts.length > 0 ? (
                      <Select
                        label="Host"
                        description="Escolha um dos hosts deste recurso."
                        placeholder="Selecione o host"
                        data={ctx.hosts}
                        value={scopeHosts[0] ?? null}
                        onChange={v => setScopeHosts(v ? [v] : [])}
                        searchable
                        error={effectiveScopeHosts.length === 0 ? 'Informe o host.' : undefined}
                        withAsterisk
                      />
                    ) : (
                      <TagsInput
                        label="Host (manual)"
                        description="O recurso não tem hosts conhecidos — informe ao menos um (ex.: portal.tectoy.com.br ou *.tectoy.com.br)."
                        placeholder="Adicionar host"
                        value={scopeHosts}
                        onChange={vals => setScopeHosts(vals.map(v => v.trim()).filter(Boolean))}
                        error={effectiveScopeHosts.length === 0 ? 'Informe ao menos um host.' : undefined}
                        withAsterisk
                      />
                    )
                  )}
                </>
              ) : (
                // ── Sem contexto: comportamento original (todos do WAF / 1 domínio)
                <>
                  <SegmentedControl
                    fullWidth
                    value={applyTo}
                    onChange={v => setApplyTo(v as 'all' | 'specific')}
                    data={[
                      { value: 'all', label: 'Todos os domínios deste WAF' },
                      { value: 'specific', label: 'Domínio específico' },
                    ]}
                  />
                  {applyTo === 'specific' && (
                    <Autocomplete
                      label="Domínio"
                      description="Escolha um domínio da lista ou digite outro (ex.: portal.tectoy.com.br ou *.tectoy.com.br)."
                      placeholder="Selecione ou digite o domínio"
                      data={domainOptions}
                      value={scopeHosts[0] ?? ''}
                      onChange={v => setScopeHosts(v ? [v] : [])}
                      error={applyTo === 'specific' && !(scopeHosts[0] ?? '').trim() ? 'Informe o domínio.' : undefined}
                      withAsterisk
                    />
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Stepper.Step>

        {/* ─── Passo 4: Revisão ──────────────────────────────────────────── */}
        <Stepper.Step label="Revisão" description="Confirme e crie">
          <Stack gap="md" mt="md">
            <Text size="sm" c="dimmed">
              Pré-visualização (o backend é a fonte da verdade ao montar o statement):
            </Text>
            {ctx.kind === 'resource' && applyTo === 'all' ? (
              <Alert color="grape" variant="light">
                Aplica a todos os hosts do recurso <b>{ctx.name || ctx.arn}</b>
                {' '}({effectiveScopeHosts.length} domínio{effectiveScopeHosts.length === 1 ? '' : 's'})
              </Alert>
            ) : effectiveScopeHosts.length === 1 ? (
              <Alert color="grape" variant="light">
                Aplica ao domínio: <b>{effectiveScopeHosts[0]}</b>
              </Alert>
            ) : effectiveScopeHosts.length > 1 ? (
              <Alert color="grape" variant="light">
                Aplica aos domínios: <b>{effectiveScopeHosts.join(', ')}</b>
              </Alert>
            ) : (
              <Text size="sm" c="dimmed">Aplica a todos os domínios deste WAF.</Text>
            )}
            <ScrollArea.Autosize mah={320}>
              <Code block style={{ fontSize: 12 }}>
                {JSON.stringify(previewObject, null, 2)}
              </Code>
            </ScrollArea.Autosize>
            {submitError && (
              <Alert color="red" withCloseButton onClose={() => setSubmitError(null)} icon={<IconAlertTriangle size={16} />}>
                {submitError}
              </Alert>
            )}
          </Stack>
        </Stepper.Step>
      </Stepper>

      {/* ─── Navegação ───────────────────────────────────────────────────── */}
      <Group justify="space-between" mt="xl">
        <Button
          variant="default"
          leftSection={<IconArrowLeft size={16} />}
          onClick={active === 0 ? handleClose : back}
        >
          {active === 0 ? 'Cancelar' : 'Voltar'}
        </Button>

        {active < 3 ? (
          <Button rightSection={<IconArrowRight size={16} />} onClick={next} disabled={!canAdvance}>
            Próximo
          </Button>
        ) : (
          <Button
            leftSection={<IconCheck size={16} />}
            color="green"
            loading={createMutation.isPending}
            disabled={!configValid || !actionValid || !params}
            onClick={() => createMutation.mutate()}
          >
            Criar
          </Button>
        )}
      </Group>
    </Modal>
  )
}
